import { useEffect, useRef, useState } from "react";
import { post, type Row } from "../api";
import { Button } from "../ui";
import { FieldError, ValidationSummary } from "./ValidationSummary";
import { useFormValidation } from "../form-validation";
import { operationError } from "../professional-operations";
import "./professional-operations.css";

const emptyCredentials = () => ({
  email: "",
  name: "",
  password: "",
  totp: "",
  recoveryCode: "",
});

/** Fragments never enter requests, browser storage, telemetry, or subsequent URLs. */
export function watchInvitationLinks(
  browser: Window,
  onToken: (token: string) => void,
) {
  const strip = () =>
    browser.history.replaceState(
      browser.history.state,
      "",
      browser.location.pathname,
    );
  strip();
  const changed = () => {
    const next =
      new URLSearchParams(browser.location.hash.slice(1)).get("token") || "";
    strip();
    onToken(next);
  };
  browser.addEventListener("hashchange", changed);
  return () => browser.removeEventListener("hashchange", changed);
}

export function PortalInvitation({ complete }: { complete: () => void }) {
  const [token, setToken] = useState(() => {
    const value =
      new URLSearchParams(window.location.hash.slice(1)).get("token") || "";
    return value;
  });
  const [values, setValues] = useState(emptyCredentials);
  const [existing, setExisting] = useState(false),
    [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [accepted, setAccepted] = useState(false);
  const generation = useRef(0),
    accepting = useRef(false);
  useEffect(
    () =>
      watchInvitationLinks(window, (next) => {
        generation.current++;
        setToken(next);
        setValues(emptyCredentials());
        setExisting(false);
        setRecovery(false);
        setAccepted(false);
        setError("");
      }),
    [],
  );
  const fields = [
    {
      id: "invite-email",
      label: "Invited email address",
      value: values.email,
      required: true,
      maxLength: 254,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      patternMessage: "Enter the email address that received the invitation.",
    },
    {
      id: "invite-name",
      label: "Full name",
      value: values.name,
      required: true,
      minLength: 2,
      maxLength: 100,
    },
    {
      id: "invite-password",
      label: existing ? "Current password" : "New password",
      value: values.password,
      required: true,
      minLength: existing ? undefined : 12,
      maxLength: 256,
    },
  ];
  const validation = useFormValidation("Accept invitation", fields);
  async function accept() {
    if (accepting.current || !token || !validation.validate()) return;
    const request = generation.current;
    accepting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await post<Row>("/portal/invitations/accept", {
        token,
        ...values,
        totp: recovery ? undefined : values.totp || undefined,
        recoveryCode: recovery ? values.recoveryCode || undefined : undefined,
      });
      if (request !== generation.current) return;
      if (result.accepted !== true || result.signInRequired !== true)
        throw new Error("Acceptance not confirmed");
      setValues(emptyCredentials());
      setAccepted(true);
    } catch (failure) {
      if (request !== generation.current) return;
      const code = (failure as Row)?.code;
      setError(
        [
          "INVITATION_UNAVAILABLE",
          "INVITATION_INVALID",
          "INVITATION_EXPIRED",
          "INVITATION_NOT_FOUND",
          "INVITATION_ALREADY_USED",
        ].includes(code)
          ? "This invitation is expired, already used or no longer valid. Ask your firm for a new invitation."
          : operationError(failure),
      );
    } finally {
      accepting.current = false;
      setBusy(false);
    }
  }
  return (
    <main className="zt-operations zt-invitation">
      <section className="card zt-operation-panel">
        <span className="eyebrow">ZentroTax · Client Portal</span>
        <h1>
          {accepted ? "Invitation accepted" : "Connect with your tax firm"}
        </h1>
        {accepted ? (
          <>
            <p>
              Your access is linked to the records shared by your firm. Sign in
              to continue; accepting an invitation does not start a session.
            </p>
            <Button variant="primary" onClick={complete}>
              Continue to sign in
            </Button>
          </>
        ) : !token ? (
          <>
            <p role="alert">
              The invitation link is missing its secure token. Open the complete
              link your firm provided, or ask for a new invitation.
            </p>
            <Button onClick={complete}>Go to sign in</Button>
          </>
        ) : (
          <>
            <p>
              Use the email address invited by your firm. This link is
              single-use. Do not forward it.
            </p>
            <label className="zt-check">
              <input
                type="checkbox"
                checked={existing}
                onChange={(event) => setExisting(event.target.checked)}
              />
              I already have a ZentroTax account
            </label>
            <ValidationSummary issues={validation.issues} />
            {error && (
              <p className="zt-operation-error" role="alert">
                {error}
              </p>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void accept();
              }}
              noValidate
            >
              {fields.map((field) => {
                const key = field.id.replace("invite-", "") as
                  "email" | "name" | "password";
                return (
                  <label className="zt-field" key={key} htmlFor={field.id}>
                    {field.label}
                    <input
                      {...validation.fieldProps(field.id)}
                      type={
                        key === "password"
                          ? "password"
                          : key === "email"
                            ? "email"
                            : "text"
                      }
                      autoComplete={
                        key === "password"
                          ? existing
                            ? "current-password"
                            : "new-password"
                          : key
                      }
                      value={values[key]}
                      disabled={busy}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                    />
                    <FieldError issue={validation.issue(field.id)} />
                  </label>
                );
              })}
              {!existing && (
                <p>
                  Use at least 12 characters. Your firm may require an
                  authenticator before access is granted.
                </p>
              )}
              {existing && (
                <>
                  <label className="zt-check">
                    <input
                      type="checkbox"
                      checked={recovery}
                      onChange={(event) => setRecovery(event.target.checked)}
                    />
                    Use a recovery code instead of an authenticator code
                  </label>
                  <label className="zt-field" htmlFor="invite-mfa">
                    {recovery
                      ? "Recovery code (if MFA is enabled)"
                      : "Authenticator code (if MFA is enabled)"}
                    <input
                      id="invite-mfa"
                      type="password"
                      autoComplete="one-time-code"
                      value={recovery ? values.recoveryCode : values.totp}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [recovery ? "recoveryCode" : "totp"]:
                            event.target.value,
                        }))
                      }
                    />
                  </label>
                </>
              )}
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? "Connecting…" : "Accept invitation"}
              </Button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
